import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

// Retorna true se tratou a navegação, false para passar ao próximo handler (LIFO).
type HandlerFn = () => Promise<boolean> | boolean;

type BackHandlerContextType = {
  pushHandler: (fn: HandlerFn) => () => void;
};

const BackHandlerContext = createContext<BackHandlerContextType | undefined>(undefined);

export const BackHandlerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const handlersRef = useRef<HandlerFn[]>([]);

  const pushHandler = useCallback((fn: HandlerFn) => {
    handlersRef.current = [...handlersRef.current, fn];
    return () => {
      handlersRef.current = handlersRef.current.filter(h => h !== fn);
    };
  }, []);

  useEffect(() => {
    // Sentinela: garante que o botão back sempre dispare popstate em vez de
    // navegar para fora do app enquanto houver handlers registrados.
    window.history.pushState(null, '');

    const onPopState = async () => {
      const stack = handlersRef.current;

      // Tenta handlers do topo para a base (LIFO).
      for (let i = stack.length - 1; i >= 0; i--) {
        const handled = await stack[i]();
        if (handled) break;
      }

      // Re-empurra sentinela para o próximo back button press.
      window.history.pushState(null, '');
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return (
    <BackHandlerContext.Provider value={{ pushHandler }}>
      {children}
    </BackHandlerContext.Provider>
  );
};

// Retorna undefined fora do provider — componentes devem checar antes de usar.
export const useBackHandler = () => useContext(BackHandlerContext);
